import React from 'react';
import type {
  PiV2Appointment,
  PiV2EstimateLine,
  PiV2VisitEstimate,
} from '../../../patient-insurance/typesV2';
import { piV2Money } from '../../../patient-insurance/utilsV2';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientInsuranceNextVisitProps {
  estimate: PiV2VisitEstimate;
  appointment: PiV2Appointment;
  onReVerify?: () => void;
}

const COLS = 'grid grid-cols-[auto_1fr_5rem_6rem_5.5rem] items-center gap-3';

/**
 * Next-visit estimate, showing the arithmetic rather than a total.
 *
 * 🔴 The gate: when `estimateIsReal` is false this renders the reason and the
 * re-verify action — never a zero, never a dash, never a plausible-looking
 * number. The estimate service has a stub fallback that fabricates figures when
 * real eligibility is not loaded, and a fabricated figure must never reach a
 * patient as a quote.
 */
export function PatientInsuranceNextVisit({
  estimate: e,
  appointment,
  onReVerify,
}: PatientInsuranceNextVisitProps) {
  const subtitle = `${appointment.whenLabel} · ${appointment.provider}`;
  if (!e.estimateIsReal) {
    return (
      <PatientProfileSectionCard
        icon="dollar"
        title="Estimate for this visit"
        accent="var(--terracotta)"
        subtitle={subtitle}
      >
        <div className="flex items-start gap-2.5 rounded-[var(--r-md)] bg-[var(--gold-tint)] px-3.5 py-3">
          <Icon kind="alert" size={15} stroke={1.9} style={{ color: 'var(--gold)', marginTop: 1, flexShrink: 0 }} />
          <div className="min-w-0">
            <p className="m-0 text-[13px] leading-[1.55] text-[var(--ink-1)]">
              {e.unavailableReason ?? 'Eligibility is not confirmed, so no estimate can be shown.'}
            </p>
            {onReVerify ? (
              <span className="mt-2.5 inline-flex">
                <Button variant="secondary" size="sm" iconLeft="refresh" onClick={onReVerify}>
                  Re-verify eligibility
                </Button>
              </span>
            ) : null}
          </div>
        </div>
      </PatientProfileSectionCard>
    );
  }

  return (
    <PatientProfileSectionCard
      icon="dollar"
      title="Estimate for this visit"
      accent="var(--success)"
      subtitle={subtitle}
    >
      <div className="mb-3.5 flex flex-wrap items-end gap-x-3 gap-y-1">
        <span className="font-[var(--font-display)] text-[30px] font-bold leading-none tracking-[-0.03em] text-[var(--success)]">
          {piV2Money(e.totals.patientEstimate)}
        </span>
        <span className="text-[12.5px] text-[var(--ink-3)]">
          estimated patient portion ·{' '}
          <span className="font-[var(--font-mono)] text-[var(--ink-2)]">
            {piV2Money(e.totals.officeFee)} fee − {piV2Money(e.totals.insuranceEstimate)} estimated
            insurance
          </span>
        </span>
      </div>

      <div className={`${COLS} pb-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]`}>
        <span>Code</span>
        <span>Procedure</span>
        <span className="text-right">Office fee</span>
        <span className="text-right">Est. insurance</span>
        <span className="text-right">Est. patient</span>
      </div>
      {e.lines.map((l: PiV2EstimateLine) => (
        <div key={l.code} className={`${COLS} border-t border-[var(--cream-2)] py-2.5 text-[12.5px]`}>
          <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">{l.code}</span>
          <span className="min-w-0 truncate font-medium text-[var(--ink-0)]">{l.label}</span>
          <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{piV2Money(l.officeFee)}</span>
          <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--success)]">
            {piV2Money(l.insuranceEstimate)}
          </span>
          <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">
            {piV2Money(l.patientEstimate)}
          </span>
        </div>
      ))}
      <div className={`${COLS} border-t-[1.5px] border-[var(--cream-shadow)] py-2.5 text-[12.5px] font-semibold`}>
        <span />
        <span className="text-[var(--ink-0)]">Totals</span>
        <span className="text-right font-[var(--font-mono)] text-[var(--ink-1)]">{piV2Money(e.totals.officeFee)}</span>
        <span className="text-right font-[var(--font-mono)] text-[var(--success)]">
          {piV2Money(e.totals.insuranceEstimate)}
        </span>
        <span className="text-right font-[var(--font-mono)] text-[var(--ink-0)]">
          {piV2Money(e.totals.patientEstimate)}
        </span>
      </div>

      <p className="m-0 mt-3 text-[11px] leading-[1.5] text-[var(--ink-4)]">
        Estimates are not a guarantee of payment. Final insurance payment and patient responsibility
        may vary.
      </p>
    </PatientProfileSectionCard>
  );
}
